import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiUpload } from 'react-icons/fi';
import { RegistrationForm } from '@features/profile-edit';
import {
  selectUser,
  selectAuthStatus,
  updateMe,
} from '../../../entities/auth/model';
import { setErrorNotification } from '../../../app/store/notifications';
import { AppDispatch } from '../../../app/store/store';



const MAX_PHOTO_BYTES = 2 * 1024 * 1024;

function getInitials(firstName?: string, lastName?: string, username?: string) {
  const s = `${(firstName || '').trim().charAt(0)}${(lastName || '')
    .trim()
    .charAt(0)}`.toUpperCase();
  if (s) return s;
  // Fallback: первая буква username, как в Header.tsx — нельзя
  // показывать «??» если у пользователя ещё нет ФИО.
  return (username || '?').trim().charAt(0).toUpperCase();
}

const AvatarBlock: React.FC = () => {
  const me = useSelector(selectUser);
  const status = useSelector(selectAuthStatus);
  const dispatch = useDispatch<AppDispatch>();
  const inputRef = useRef<HTMLInputElement>(null);

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      dispatch(setErrorNotification('Файл больше 2 МБ. Сожмите и попробуйте снова.'));
      // Сбрасываем value, иначе onChange не сработает при повторном
      // выборе того же файла.
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    const formData = new FormData();
    formData.append('photo', file);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(updateMe({ data: formData } as any));
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDelete = () => {
    const formData = new FormData();
    formData.append('clear_photo', 'true');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dispatch(updateMe({ data: formData } as any));
  };

  if (!me) return null;

  const hasPhoto = !!me.photo;
  const initials = getInitials(me.first_name, me.last_name, me.username);
  const fullName = `${me.first_name || ''} ${me.last_name || ''}`.trim() || me.username;

  return (
    <div className="flex items-center gap-5 pb-6 border-b border-gray-100">
      {/* Аватар: фото если есть, иначе светлый круг с инициалами */}
      {hasPhoto ? (
        <img
          src={me.photo as string}
          alt="avatar"
          className="w-20 h-20 rounded-full object-cover"
        />
      ) : (
        <div className="w-20 h-20 rounded-full bg-[#FCE9E9] flex items-center justify-center text-[#e3282a] text-2xl font-semibold">
          {initials}
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <div className="text-xl font-semibold text-[#141414]">{fullName}</div>
        <div className="flex items-center gap-4 text-sm">
          {/* «Загрузить фото» — текстовая кнопка с tooltip-подсказкой
              «JPG или PNG, до 2 МБ». Подсказка показывается при
              hover/focus (native title даёт минимальный tooltip без
              библиотек; если макетчик хочет красивый — отдельная
              задача с tooltip-компонентом). */}
          <label
            title="JPG или PNG, до 2 МБ"
            className="inline-flex items-center gap-1.5 text-[#6E7173] hover:text-[#141414] cursor-pointer"
          >
            <FiUpload size={14} />
            Загрузить фото
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={onPickFile}
              disabled={status === 'loading'}
            />
          </label>
          {/* «Удалить фото» — появляется только если фото есть.
              По макету: hover-фон #FCE9E9, цвет акцентный. */}
          {hasPhoto && (
            <button
              type="button"
              onClick={onDelete}
              disabled={status === 'loading'}
              className="px-2 py-1 rounded-md text-[#6E7173] hover:text-[#e3282a] hover:bg-[#FCE9E9] bg-transparent border-0 cursor-pointer disabled:opacity-50"
            >
              Удалить фото
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const ProfileSettings: React.FC = () => {
  const me = useSelector(selectUser);

  if (!me) {
    return (
      <div className="bg-white rounded-xl p-8 shadow-sm text-center text-sm text-gray-500">
        Профиль загружается…
      </div>
    );
  }

  return (
    // Карточка профиля — в общем стиле helpdesk-вкладок
    // (bg-white rounded-xl shadow-sm px-6 py-5). Заголовки секций
    // («Личные данные», «Вход в систему») и разделитель между ними
    // живут внутри RegistrationForm — там же, где и сами поля.
    <div className="bg-white rounded-xl shadow-sm px-6 py-5">
      <AvatarBlock />
      <div className="mt-6">
        <RegistrationForm me={me} />
      </div>
    </div>
  );
};

export default ProfileSettings;
